import {
  Project,
  UpdateProject,
  ProjectCreate,
  ProjectVoterCount,
  ProjectVoterCountParams,
  ProjectDispositionCounts,
  ListPrecinctCount,
  UseProjectSSEUpdatesOptions,
  ProjectLoadingStatusPayload,
  RosLoadProjectVoterlist,
  SendManagedProjectResponse,
  SendManagedProjectPayload,
} from 'src/@types/project';
import { useGet, usePut } from './templateHooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAxios } from './useFetch';
import { ROS_URL, FAST_API_URL } from 'src/config';
import { useSSE } from './useSSE';
import { useCallback } from 'react';

export const useProjects = (config = {}, params = { include_archived: false }) =>
  useGet<Project[], { include_archived: boolean }>({
    queryKey: ['projects'],
    endpoint: '/v3/projects',
    url: FAST_API_URL,
    config: config,
    params: params,
  });

export const useProject = (resourceId: string, params = { include_archived: false }, config = {}) =>
  useGet<Project, { include_archived: boolean }>({
    queryKey: ['projects-detail', resourceId],
    endpoint: '/v3/projects',
    url: FAST_API_URL,
    config: config,
    resourceId: resourceId,
    params: params,
  });

export const useCreateProject = () => {
  const queryClient = useQueryClient();
  const mutationFn = useAxios<Project[], ProjectCreate>({
    endpoint: '/v2/projects',
    method: 'post',
    url: FAST_API_URL,
  });
  return useMutation({
    mutationFn,
    onSuccess: (newProjects) => {
      queryClient.setQueriesData<Project[]>({ queryKey: ['projects'] }, (oldProjects) => {
        if (!oldProjects) return newProjects;
        return [...oldProjects, ...newProjects];
      });
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['conditional_surveys'] });
      queryClient.invalidateQueries({ queryKey: ['surveys'] });
      queryClient.invalidateQueries({ queryKey: ['surveys', 'combined'] });
    },
  });
};

export const useListPrecinctCounts = (resourceId: string, config = {}) =>
  useGet<ListPrecinctCount[]>({
    queryKey: ['lists', resourceId, 'precincts'],
    endpoint: '/v1/lists/precincts',
    config: {
      resourceId: resourceId,
      ...config,
    },
    url: FAST_API_URL,
    resourceId: resourceId,
  });

export function useUpdateProject() {
  const queryClient = useQueryClient();

  const updateFn = useAxios<Project, UpdateProject>({
    endpoint: '/v1/projects',
    method: 'put',
    url: FAST_API_URL,
  });

  return useMutation({
    mutationFn: ({ data, resourceId }: { data: UpdateProject; resourceId: string }) =>
      updateFn({ data, resourceId }),
    onSuccess: (_project, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects-detail', variables.resourceId] });
    },
  });
}

export const useDeleteProjects = () => {
  const queryClient = useQueryClient();

  const deleteFn = useAxios<string, { ids: string[] }>({
    endpoint: '/v1/projects',
    method: 'delete',
    url: FAST_API_URL,
  });

  return useMutation({
    mutationFn: (variables) => deleteFn({ data: variables }),
    onMutate: async (data: { ids: string[] }) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['projects'] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<Project[]>(['projects']);

      // Optimistically update to the new value
      queryClient.setQueryData<Project[]>(['projects'], (items) => {
        if (!items) return [];
        return items.filter((i) => !data.ids.includes(i.id));
      });

      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['conditional_surveys'] });
      queryClient.invalidateQueries({ queryKey: ['surveys'] });
      queryClient.invalidateQueries({ queryKey: ['surveys', 'combined'] });

      // Return a context object with the snapshotted value
      return { previousData };
    },
    // If the mutation fails,
    // use the context returned from onMutate to roll back
    onError: (err, id, context) => {
      queryClient.setQueryData(['projects'], context?.previousData);
    },
    onSuccess: () => {
      // Invalidate both cached views: archived=true and archived=false
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
};

export const useUnarchiveProject = () => {
  const queryClient = useQueryClient();
  const updateUnarchiveMutation = usePut<null, Project>(
    ['projects'],
    '/v1/projects/unarchive',
    {},
    undefined,
    FAST_API_URL
  );

  const unarchiveProject = async (projectId: string) => {
    await updateUnarchiveMutation.mutateAsync({ data: null, resourceId: projectId });
    queryClient.invalidateQueries({ queryKey: ['projects'] });
  };

  return { unarchiveProject, ...updateUnarchiveMutation };
};

export const useProjectVoterCount = (projectId: string, config = {}, params = {}) =>
  useGet<ProjectVoterCount, ProjectVoterCountParams>({
    queryKey: ['project-voter-count', projectId],
    endpoint: `/v1/projects/${projectId}/counts`,
    config: config,
    params: params,
    url: FAST_API_URL,
  });

export const useRefreshProject = (projectId: string) => {
  const queryClient = useQueryClient();

  const fetch = useAxios<undefined, undefined>({
    endpoint: `/v1/projects/${projectId}/refresh`,
    method: 'post',
    url: FAST_API_URL,
  });
  return useMutation({
    mutationFn: () => fetch({}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
};

export const useRosLoadProjectVoterlist = () => {
  const mutationFn = useAxios<null, RosLoadProjectVoterlist>({
    endpoint: '/load_project',
    method: 'post',
    url: ROS_URL,
  });
  return useMutation({ mutationFn });
};

export function useSendOutreach() {
  const queryClient = useQueryClient();
  const mutationFn = useAxios<SendManagedProjectResponse, SendManagedProjectPayload>({
    endpoint: `/v1/projects/send`,
    method: 'post',
    url: FAST_API_URL,
  });
  return useMutation({
    mutationFn,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });

      const projectQueryKey = ['project', data.project.id];
      queryClient.setQueryData(projectQueryKey, data);
    },
  });
}

export const useCanvassProjectDispositionCounts = (resourceId: string, config = {}) =>
  useGet<ProjectDispositionCounts[]>({
    queryKey: ['project-disposition-counts', resourceId],
    endpoint: `/v1/interactions/canvass/${resourceId}/disposition-counts`,
    url: FAST_API_URL,
    config: config,
  });

/**
 * Hook to listen for real-time project loading status updates via SSE.
 *
 * Automatically invalidates relevant React Query caches when projects are ready or failed.
 * Handles reconnection on network changes and errors.
 *
 * @example
 * ```tsx
 * function ProjectDashboard() {
 *   const { data: statusUpdate, isConnected, error } = useProjectSSEUpdates({
 *     enabled: true,
 *     onStatusUpdate: (status) => {
 *       if (status.status === 'ready') {
 *         toast.success(`Project ${status.project_id} is ready!`);
 *       }
 *     }
 *   });
 *
 *   return (
 *     <div>
 *       <StatusIndicator connected={isConnected} />
 *       {statusUpdate && (
 *         <div>Last update: {statusUpdate.project_id} - {statusUpdate.status}</div>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export function useProjectSSEUpdates(options: UseProjectSSEUpdatesOptions = {}) {
  const { enabled = true, onStatusUpdate, onConnect, onDisconnect, onError } = options;

  const queryClient = useQueryClient();

  const handleMessage = useCallback(
    (data: ProjectLoadingStatusPayload) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      queryClient.cancelQueries({ queryKey: ['projects'] });

      // Call user's callback if provided
      onStatusUpdate?.(data);

      // setQueriesData will work on anything that matches a query that starts with 'projects'
      // when we pass to the server, the query parameters are automatically addded a part of the query
      // key so we need to update every varaint of a query like this
      queryClient.setQueriesData<Project[]>({ queryKey: ['projects'] }, (oldProjects) => {
        if (!oldProjects) return oldProjects;

        const projects = Array.isArray(oldProjects) ? oldProjects : [oldProjects];
        const updatedProjects = projects.map((project) =>
          project.id === data.project_id
            ? { ...project, project_loading_status: data.status }
            : project
        );

        return updatedProjects;
      });

      // Try to set the detailed project data if it's cached
      queryClient.setQueriesData<Project>(
        { queryKey: ['projects-detail', data.project_id] },
        (oldProject) => {
          if (!oldProject) return oldProject;
          if (data.status === 'ready') {
            queryClient.invalidateQueries({ queryKey: ['outreach_status', data.project_id] });
          }
          return { ...oldProject, project_loading_status: data.status };
        }
      );

      queryClient.invalidateQueries({ queryKey: ['project-voter-count', data.project_id] });
      queryClient.invalidateQueries({
        queryKey: ['project-disposition-counts', data.project_id],
      });
    },
    [queryClient, onStatusUpdate]
  );

  const handleConnect = useCallback(() => {
    console.log('Connected to project SSE updates');
    onConnect?.();
  }, [onConnect]);

  const handleDisconnect = useCallback(() => {
    console.log('Disconnected from project SSE updates');
    onDisconnect?.();
  }, [onDisconnect]);

  const handleError = useCallback(
    (error: Error) => {
      console.error('Project SSE connection error:', error);
      onError?.(error);
    },
    [onError]
  );

  return useSSE<ProjectLoadingStatusPayload>('v1/projects/sse/updates', {
    enabled,
    onMessage: handleMessage,
    onConnect: handleConnect,
    onDisconnect: handleDisconnect,
    onError: handleError,
    reconnectInterval: 3000, // Reconnect after 3 seconds on error
  });
}
